import { ALL_LOCATIONS_ID, ALL_DEALERSHIPS_ID } from '../util/locationUtils';
import { HTTPService } from './httpService';

const KEY_USER = 'userAPI';
const KEY_COMPANY = 'companyAPI';
const KEY_DEALER_GROUP = 'dealerGroupAPI';
const KEY_SCOPE = 'scopeAPI';
const KEY_STRIPE = 'stripeCapabilities';

const API = new HTTPService();

function readJSON(key) {
  try {
    return JSON.parse(localStorage.getItem(key));
  } catch {
    return null;
  }
}

function writeJSON(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch { /* no-op */ }
}

function clearKey(key) {
  try { localStorage.removeItem(key); } catch { /* no-op */ }
}

const ScopeService = {

  // ------------------------------------------------------------------
  // User
  // ------------------------------------------------------------------

  getUser() {
    return readJSON(KEY_USER);
  },

  getUserId() {
    return readJSON(KEY_USER)?.User_id ?? null;
  },

  getUserRole() {
    return readJSON(KEY_USER)?.role ?? null;
  },

  // ------------------------------------------------------------------
  // Scope tree (full server response)
  // ------------------------------------------------------------------

  /**
   * Fetch the user's scope tree from /users/:userId/scope and cache it.
   * Does NOT activate a scope — caller picks an index and calls activateScopeByIndex.
   */
  async loadScopeTree(userId) {
    const { data } = await API.get(`/users/${userId}/scope`);
    const payload = data || {};
    const tree = {
      userId: payload.userId ?? userId,
      userType: payload.userType || 'installer',
      scopes: Array.isArray(payload.scopes) ? payload.scopes : [],
      defaultScopeIndex: Number.isInteger(payload.defaultScopeIndex) ? payload.defaultScopeIndex : 0,
      activeScopeIndex: null,
    };
    writeJSON(KEY_SCOPE, tree);
    return tree;
  },

  getScopeTree() {
    return readJSON(KEY_SCOPE);
  },

  getAllScopes() {
    return readJSON(KEY_SCOPE)?.scopes || [];
  },

  getActiveScopeIndex() {
    const tree = readJSON(KEY_SCOPE);
    if (!tree) return null;
    return tree.activeScopeIndex ?? tree.defaultScopeIndex ?? 0;
  },

  /**
   * Resolve & activate a scope by index. Fetches the entity record
   * (installer / dealer) so back-compat companyAPI is populated, then
   * writes companyAPI + dealerGroupAPI from the combined data.
   *
   * Returns the activated scope.
   */
  async activateScopeByIndex(index) {
    const tree = readJSON(KEY_SCOPE);
    if (!tree) throw new Error('ScopeService: no scope tree loaded');
    const scope = tree.scopes[index];
    if (!scope) throw new Error(`ScopeService: scope index ${index} out of range`);

    tree.activeScopeIndex = index;
    writeJSON(KEY_SCOPE, tree);

    await this._writeBackCompatRecords(scope);
    return scope;
  },

  /**
   * Find a scope by (type, id) and activate it. Returns the scope
   * (or null if not found).
   */
  async activateScopeByTypeId(type, id) {
    const scopes = this.getAllScopes();
    const idx = scopes.findIndex(s => s.type === type && String(s.id) === String(id));
    if (idx < 0) return null;
    return this.activateScopeByIndex(idx);
  },

  /**
   * Re-fetch and re-activate the current scope. Useful after the entity
   * record changes (e.g., user toggles time_tracking_mode in profile).
   */
  async refreshActiveScope() {
    const idx = this.getActiveScopeIndex();
    if (idx === null) return null;
    return this.activateScopeByIndex(idx);
  },

  /**
   * Internal: fetches the entity record for a scope and writes
   * companyAPI + dealerGroupAPI in the legacy shape so all existing
   * consumers (read via getCompany / direct localStorage) keep working.
   *
   * company_type enum is now ONLY 'installer' or 'dealer'. Sub-scoping is
   * derived from scope.scopedId (server-side `user_companies.location_id`):
   *   installer + scopedId=0   → all locations under the installer
   *   installer + scopedId>0   → pinned to that single location
   *   dealer    + scopedId=0   → all dealers in the group
   *   dealer    + scopedId>0   → pinned to that single dealer
   * For `dealer`, scope.id is always the group_id.
   */
  async _writeBackCompatRecords(scope) {
    if (!scope) return;
    const type = scope.type;
    const subScoped = scope.scopedId != null && Number(scope.scopedId) > 0;

    if (type === 'installer') {
      const { data } = await API.get(`/installers/${scope.id}`);
      const installerRow = (Array.isArray(data) ? data[0] : data) || {};

      // Server returns scope.locations pre-filtered: full list when scopedId=0,
      // single-entry array when scoped to one location.
      const locations = scope.locations || [];

      // When pinned to a single location, set selectedLocationId so downstream
      // code shows that location. Otherwise null (= "All Locations").
      const selectedLocationId = subScoped ? scope.scopedId : null;

      const companyRecord = {
        ...installerRow,
        Installer_id: scope.id,
        Installer_Name: installerRow.Installer_Name || installerRow.installer_name || scope.name,
        locations,
        selectedLocationId,
      };

      writeJSON(KEY_COMPANY, companyRecord);
      clearKey(KEY_DEALER_GROUP);
      return;
    }

    if (type === 'dealer') {
      // scope.id is the group_id. scope.dealerships is the (possibly filtered)
      // list of dealers the user can see. scope.scopedId pins to one dealer
      // (or 0 = all). A "single dealer" user is just a group user pinned to
      // a single dealership.
      const dealerships = scope.dealerships || [];
      const pinnedDealer = subScoped
        ? dealerships.find(d => String(d.dealer_id) === String(scope.scopedId))
        : null;
      const firstDealership = pinnedDealer || dealerships[0] || {};

      writeJSON(KEY_COMPANY, {
        ...firstDealership,
        dealer_id: firstDealership.dealer_id ?? (subScoped ? scope.scopedId : null),
        dealer_name: firstDealership.dealer_name || scope.name,
      });
      writeJSON(KEY_DEALER_GROUP, {
        isDealerGroup: !subScoped,
        groupId: scope.id,
        groupName: scope.name,
        selectedDealerId: subScoped ? scope.scopedId : ALL_DEALERSHIPS_ID,
        dealerships,
      });
    }
  },

  /**
   * Wipes scope-related localStorage (e.g., on sign-out).
   */
  clearAll() {
    [KEY_SCOPE, KEY_COMPANY, KEY_DEALER_GROUP, KEY_STRIPE].forEach(clearKey);
  },

  // ------------------------------------------------------------------
  // Active-scope derivations (read by everywhere downstream)
  // ------------------------------------------------------------------

  /**
   * Active scope object from the cached tree (preferred).
   * Falls back to a synthesized scope from companyAPI/dealerGroupAPI for code
   * paths that haven't migrated to scope-tree-aware reads yet.
   */
  getActiveScope() {
    const tree = readJSON(KEY_SCOPE);
    if (tree && Array.isArray(tree.scopes)) {
      const idx = tree.activeScopeIndex ?? tree.defaultScopeIndex ?? 0;
      const fromTree = tree.scopes[idx];
      if (fromTree) return fromTree;
    }
    // Legacy fallback — derive from companyAPI / dealerGroupAPI
    return this._legacyActiveScope();
  },

  _legacyActiveScope() {
    const userType = this.getUserType();
    const company = readJSON(KEY_COMPANY);
    const dgInfo = readJSON(KEY_DEALER_GROUP);

    if (userType === 'dealer') {
      // A dealer scope always points at a group_id. selectedDealerId on
      // dealerGroupAPI tells us whether the user is sub-scoped to a single
      // dealer (scopedId > 0) or has full group access (scopedId = 0/null).
      const selectedDealerId = dgInfo?.selectedDealerId;
      const isSubScoped = selectedDealerId != null && selectedDealerId !== ALL_DEALERSHIPS_ID;
      return {
        type: 'dealer',
        id: dgInfo?.groupId ?? company?.dealer_id ?? null,
        name: dgInfo?.groupName || company?.dealer_name || '',
        role: this.getUserRole(),
        scopedId: isSubScoped ? selectedDealerId : 0,
        scopedName: null,
        dealerships: dgInfo?.dealerships || [],
      };
    }
    return {
      type: 'installer',
      id: company?.Installer_id ?? null,
      name: company?.Installer_Name || '',
      role: this.getUserRole(),
      scopedId: company?.selectedLocationId ?? 0,
      scopedName: null,
      locations: company?.locations || [],
    };
  },

  /**
   * Active scope's role (per-row from user_companies). Falls back to the
   * global user.role when the scope tree isn't loaded yet.
   */
  getActiveScopeRole() {
    return this.getActiveScope()?.role || this.getUserRole();
  },

  /**
   * Returns the top-level company type for the active user: 'installer' or 'dealer'.
   * This matches the server-side `user_companies.company_type` enum, which collapsed
   * from 4 values (installer / installer_location / dealer / dealer_group) to 2 —
   * the location vs group distinction is now derived from scope.scopedId.
   */
  getUserType() {
    const tree = readJSON(KEY_SCOPE);
    if (tree?.userType) return tree.userType;
    // Legacy fallback
    const dgInfo = readJSON(KEY_DEALER_GROUP);
    if (dgInfo?.groupId) return 'dealer';
    const user = readJSON(KEY_USER);
    const raw = (user?.User_type || '').toLowerCase();
    if (raw === 'dealer') return 'dealer';
    return 'installer';
  },

  /**
   * True when the active scope is a dealer-side scope with FULL group access
   * (location_id = 0 on the server-side user_companies row). Sub-scoped dealer
   * users (pinned to one dealership) return false — to them the app behaves
   * like the legacy single-dealer experience.
   */
  isDealerGroupUser() {
    return Boolean(readJSON(KEY_DEALER_GROUP)?.isDealerGroup);
  },

  /**
   * True when the active scope is an installer-side scope pinned to a single
   * location (server-side location_id > 0). Equivalent to the retired
   * 'installer_location' company_type.
   */
  isLocationScopedInstaller() {
    if (this.getUserType() !== 'installer') return false;
    const company = readJSON(KEY_COMPANY);
    const v = company?.selectedLocationId;
    return v != null && v !== ALL_LOCATIONS_ID;
  },

  // ------------------------------------------------------------------
  // Back-compat accessors (companyAPI / dealerGroupAPI)
  // ------------------------------------------------------------------

  getCompany() {
    return readJSON(KEY_COMPANY);
  },

  getCompanyName() {
    const scope = this.getActiveScope();
    if (scope?.name) return scope.name;
    const company = readJSON(KEY_COMPANY);
    if (this.isDealerGroupUser()) {
      return readJSON(KEY_DEALER_GROUP)?.groupName || company?.dealer_name || '';
    }
    return company?.Installer_Name || company?.dealer_name || '';
  },

  getEntityId() {
    const scope = this.getActiveScope();
    if (scope?.id != null) return scope.id;
    const company = readJSON(KEY_COMPANY);
    return company?.Installer_id ?? company?.dealer_id ?? null;
  },

  getLocations() {
    const scope = this.getActiveScope();
    if (scope?.type === 'installer') return scope.locations || [];
    return readJSON(KEY_COMPANY)?.locations || [];
  },

  getSelectedLocationId() {
    const company = readJSON(KEY_COMPANY);
    const val = company?.selectedLocationId;
    return val === undefined || val === null ? ALL_LOCATIONS_ID : val;
  },

  setSelectedLocationId(locationId) {
    const company = readJSON(KEY_COMPANY);
    if (!company) return;
    company.selectedLocationId = locationId === ALL_LOCATIONS_ID ? null : locationId;
    writeJSON(KEY_COMPANY, company);
  },

  getDealerGroup() {
    return readJSON(KEY_DEALER_GROUP);
  },

  getGroupId() {
    return readJSON(KEY_DEALER_GROUP)?.groupId ?? null;
  },

  getGroupName() {
    return readJSON(KEY_DEALER_GROUP)?.groupName || '';
  },

  getSelectedDealerId() {
    const dgInfo = readJSON(KEY_DEALER_GROUP);
    if (!dgInfo) return ALL_DEALERSHIPS_ID;
    const val = dgInfo.selectedDealerId;
    return val === undefined || val === null ? ALL_DEALERSHIPS_ID : val;
  },

  setSelectedDealerId(dealerId) {
    const dgInfo = readJSON(KEY_DEALER_GROUP) || {};
    dgInfo.selectedDealerId = dealerId === ALL_DEALERSHIPS_ID ? ALL_DEALERSHIPS_ID : dealerId;
    writeJSON(KEY_DEALER_GROUP, dgInfo);
  },

  // ------------------------------------------------------------------
  // Multi-location rollups (capacity / calendar auth)
  // ------------------------------------------------------------------

  /**
   * Effective capacity for the active scope.
   * - installer + pinned to one location: that location's capacity
   * - installer + "all locations": sum across locations (treats company as a fleet of bays)
   * - dealer: 0 (concept doesn't apply)
   */
  getEffectiveCapacity() {
    const scope = this.getActiveScope();
    if (!scope) return 0;
    if (scope.type === 'installer') {
      const locations = scope.locations || [];
      const selected = this.getSelectedLocationId();
      if (selected !== ALL_LOCATIONS_ID) {
        const loc = locations.find(l => String(l.location_id) === String(selected));
        return Number(loc?.capacity) || 0;
      }
      return locations.reduce((sum, l) => sum + (Number(l.capacity) || 0), 0);
    }
    return 0;
  },

  /**
   * True only when every location in scope has Google Calendar OAuth connected.
   * For dealer scopes, returns true (concept doesn't apply).
   */
  isCalendarAuthenticated() {
    const scope = this.getActiveScope();
    if (!scope) return false;
    if (scope.type === 'installer') {
      const locations = scope.locations || [];
      if (!locations.length) return false;
      return locations.every(l => Number(l.authenticated) === 1);
    }
    return true;
  },
};

export default ScopeService;
